/**
 * The hires the board refused to guess at.
 *
 * Matching a hire to the colleague they became is done on a NAME, and a name is
 * not an identifier. Two people share one; somebody who has left has none in the
 * people database at all. A wrong merge hides a real person and moves their FTE
 * to another team, and neither is visible once it has happened — so the join
 * asks rather than assumes, and this is where it asks.
 *
 * THREE ANSWERS, and the third is not "none of the above":
 *
 *   · this one       — the colleague it is. Matching refused; a person knows.
 *   · keep separate  — a real answer, not a deferral. A hire who has since left
 *                      HAS no colleague, and without a stored no the board would
 *                      raise them again on every load.
 *   · undo           — clears the decision so matching decides again. Every
 *                      answer here is reversible, which is what makes it safe to
 *                      settle a queue of them quickly.
 *
 * Nothing is written to the Hiring Database or the people database. The answer
 * is an overlay applied on read, like `placements`.
 */
import { useMemo, useState } from 'react';
import type { HireReviewItem, RosterMember } from '../../../types/taDemand';
import { Caret } from './demandUi';

/** Candidates for a manual link, narrowed as the lead types. */
function search(roster: RosterMember[], q: string): RosterMember[] {
  const needle = q.trim().toLowerCase();
  if (needle.length < 2) return [];
  return roster.filter((m) => m.name.toLowerCase().includes(needle)).slice(0, 6);
}

function Person({ m }: { m: RosterMember }) {
  return (
    <span className="min-w-0">
      <span className="font-medium text-gray-900">{m.name}</span>
      <span className="text-gray-500">
        {m.position ? ` · ${m.position}` : ''}
        {m.team ? ` · ${m.team}` : ''}
        {m.unit ? ` · ${m.unit}` : ''}
      </span>
    </span>
  );
}

const pill =
  'shrink-0 rounded-sm border px-2 py-0.5 text-[11px] font-semibold disabled:opacity-50 disabled:cursor-wait';
const primary = `${pill} border-gray-900 bg-gray-900 text-white hover:bg-gray-700`;
const quiet = `${pill} border-gray-200 text-gray-600 hover:bg-gray-50`;

function ReviewItem({
  item,
  roster,
  decided,
  busy,
  onDecide,
}: {
  item: HireReviewItem;
  roster: RosterMember[];
  decided: boolean;
  busy: boolean;
  onDecide: (personId: string | null | undefined) => void;
}) {
  const [q, setQ] = useState('');
  const hits = useMemo(() => search(roster, q), [roster, q]);
  return (
    <li className="border-t border-gray-100 px-3 py-2 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[12px]">
        <span className="font-medium text-gray-900">{item.name}</span>
        <span className="text-gray-600">{item.title}</span>
        <span className="text-gray-400">{item.unit}</span>
        {item.kind === 'ambiguous' ? (
          <span className="rounded-sm bg-amber-50 px-1.5 text-[11px] font-medium leading-4 text-amber-700">
            two colleagues share this name
          </span>
        ) : (
          <span className="rounded-sm bg-gray-100 px-1.5 text-[11px] font-medium leading-4 text-gray-600">
            started {item.startDate ?? ''} · nobody in the people database by that name
          </span>
        )}
      </div>

      {decided ? (
        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
          <span>Settled.</span>
          <button type="button" className={quiet} disabled={busy} onClick={() => onDecide(undefined)}>
            Undo
          </button>
        </div>
      ) : (
        <div className="mt-1.5 space-y-1.5">
          {/* The named candidates first: for an ambiguity these ARE the answer. */}
          {(item.candidates ?? []).map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 text-[12px]">
              <Person m={c as RosterMember} />
              <button type="button" className={primary} disabled={busy} onClick={() => onDecide(c.id)}>
                This one
              </button>
            </div>
          ))}

          {/* And for a hire with no match at all, the roster is searched by hand. */}
          {item.kind === 'unmatched' && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
                Find them:
                <input
                  className="w-52 rounded-sm border border-gray-200 px-1.5 py-0.5 text-[12px] text-gray-900"
                  value={q}
                  placeholder="type a name"
                  aria-label={`Find the colleague hired as ${item.title}`}
                  onChange={(e) => setQ(e.target.value)}
                />
              </label>
              {hits.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={primary}
                  disabled={busy}
                  onClick={() => onDecide(m.id)}
                >
                  {m.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button type="button" className={quiet} disabled={busy} onClick={() => onDecide(null)}>
              Keep separate
            </button>
            <span className="text-[11px] text-gray-400">
              {item.kind === 'unmatched'
                ? 'They have left, or they are not in the people database — the role row stands as history.'
                : 'Neither of these is them.'}
            </span>
          </div>
        </div>
      )}
    </li>
  );
}

export function HireReview({
  items,
  roster,
  links,
  busyId,
  onDecide,
}: {
  items: HireReviewItem[];
  roster: RosterMember[];
  links: Record<string, string | null>;
  busyId: string | null;
  onDecide: (roleId: string, personId: string | null | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const outstanding = items.filter((i) => !Object.hasOwn(links, i.roleId));
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <Caret open={open} />
        <span className="text-[12px] font-semibold text-gray-900">
          {outstanding.length
            ? `${outstanding.length} hire${outstanding.length === 1 ? '' : 's'} to match to a colleague`
            : 'All hires matched'}
        </span>
        <span className="text-[11px] text-gray-500">
          — the board will not guess which person a shared name belongs to
        </span>
      </button>
      {open && (
        <ul>
          {items.map((i) => (
            <ReviewItem
              key={i.roleId}
              item={i}
              roster={roster}
              decided={Object.hasOwn(links, i.roleId)}
              busy={busyId === i.roleId}
              onDecide={(personId) => onDecide(i.roleId, personId)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
